import { Button } from "@greeter/matter";
import { doNothing } from "@greeter/util";
import Input from "@greeter-venue/components/Input";
import { InputProps } from "@greeter-venue/components/Input/Input";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { first } from "@greeter-venue/utility";

import css from "./UploadInput.module.scss";

export type On = () => void;
export type OnFile = (file: File | undefined) => void;
export type InputController = {
  show: () => void;
};
type OnInputController = (ctrl: InputController) => void;
type HiddenUploadInputProps = {
  onFile?: OnFile;
  onClose?: On;
  onController?: OnInputController;
  accept?: string | "image/*" | "video/*";
  open?: boolean;
};
export function HiddenUploadInput(props: HiddenUploadInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.open) ref.current?.showPicker();
  }, [props.open]);

  useEffect(() => {
    if (props.onController)
      props.onController({ show: () => ref.current?.showPicker() });
  }, []);

  useEffect(() => {
    function onChange() {
      const file = ref.current?.files?.item(0);
      if (props.onFile && file) props.onFile(file);
      if (props.onClose) props.onClose();
    }
    ref.current?.addEventListener("change", onChange);

    return () => {
      ref.current?.removeEventListener("change", onChange);
    };
  }, []);

  return (
    <input
      type="file"
      accept={props.accept}
      ref={ref}
      style={{ display: "none" }}
    />
  );
}

export type UploadButtonProps = HiddenUploadInputProps &
  PropsWithChildren & {
    onFile?: OnFile;
  };
export const UploadButton = (props: UploadButtonProps) => {
  const [ctrl, setCtrl] = useState<InputController>();

  return (
    <div onClick={ctrl?.show}>
      {props.children}
      <HiddenUploadInput onController={setCtrl} onFile={props.onFile} />
    </div>
  );
};

export type UploadInputProps = Omit<InputProps, "type" | "placeholder"> & {
  onClick?: () => void;
  onFileChosen?: (file: File | undefined) => void;
  placeholder?: string;
};

export const UploadInput: React.FC<UploadInputProps> = ({
  children,
  placeholder = "Et produktbillede u. logo",
  onClick = doNothing,
  onFileChosen = doNothing,
  ...props
}) => {
  const fileUploadRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<FileList | null>();

  const handleClick = useCallback(() => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  }, []);

  useEffect(() => {
    const ref = fileUploadRef.current;
    const handleChange = () => {
      setFiles(ref?.files);
    };

    ref?.addEventListener("change", handleChange);

    return () => ref?.removeEventListener("change", handleChange);
  }, [fileUploadRef]);

  useEffect(() => {
    onFileChosen(first(Array.from(files ?? [])));
  }, [files]);

  return (
    <Input
      {...props}
      readOnly
      placeholder={placeholder}
      type="text"
      onClick={handleClick}
      afterSlot={
        <>
          <div className={css.ButtonContainer}>
            <Button onClick={handleClick} className={css.Button}>
              {children ?? "Upload"}
            </Button>
          </div>
          <input type="file" ref={fileUploadRef} style={{ display: "none" }} />
        </>
      }
    ></Input>
  );
};
